// Parses the aiConfig.quickReplies contract: the workspace prompt asks the model to end a reply
// with one fenced block, opened by three backticks plus "ui", holding a single JSON object -
// {"widget":"quick_replies","props":{"options":["...","..."]}} - which is stripped from the body
// and rendered as chips instead (see DEMO-MAIS-AI-SETUP.md for the prompt snippet). The lookahead
// keeps a real code block tagged e.g. "uint" or "ui-kit" from matching.
const UI_BLOCK = /```ui(?![\w-])\s*([\s\S]*?)```/;
const UI_FENCE = /```ui(?![\w-])/;
// A trailing "`", "``", "```" or "```u" might still become an opening fence on the next chunk.
const PARTIAL_UI_FENCE = /`{1,3}u?$/;
const ALLOWED_WIDGETS = new Set(['quick_replies']);

export const MAX_AI_QUICK_REPLIES = 4;
export const MAX_AI_QUICK_REPLY_LENGTH = 40;

export interface AiReplyParts {
  text: string;
  quickReplies: string[];
}

// Splits a finished assistant reply into its prose and validated chip labels. Chip labels are
// model output, so only an allow-listed widget is accepted and every label is length-capped; a
// malformed block keeps the prose and drops the chips rather than failing the turn. A block
// with no closing fence (a truncated reply) is cut off the same way, so raw JSON never renders.
export function parseAiReply(raw: string): AiReplyParts {
  const match = raw.match(UI_BLOCK);
  if (!match) {
    const openFence = raw.search(UI_FENCE);
    return openFence === -1
      ? { text: raw, quickReplies: [] }
      : { text: raw.slice(0, openFence).trim(), quickReplies: [] };
  }
  const text = raw.replace(UI_BLOCK, '').trim();
  try {
    const block = JSON.parse(match[1]);
    if (!ALLOWED_WIDGETS.has(block?.widget) || !Array.isArray(block.props?.options)) {
      return { text, quickReplies: [] };
    }
    return { text, quickReplies: normalizeAiQuickReplies(block.props.options) };
  } catch {
    return { text, quickReplies: [] };
  }
}

// Shared by model-suggested chips and aiConfig.greeting.options, so both follow the same rules:
// strings only, trimmed, blanks and over-length labels dropped, at most MAX_AI_QUICK_REPLIES kept.
export function normalizeAiQuickReplies(options: readonly unknown[]): string[] {
  return options
    .filter((option): option is string => typeof option === 'string')
    .map(option => option.trim())
    .filter(option => option && option.length <= MAX_AI_QUICK_REPLY_LENGTH)
    .slice(0, MAX_AI_QUICK_REPLIES);
}

// While a reply is still streaming: hides everything from the opening fence onward, and holds
// back a trailing partial fence until the next chunk shows what it is, so the block's raw JSON
// never flashes on screen before parseAiReply() strips it at close.
export function visibleAiReplyPortion(partial: string): string {
  const openFence = partial.search(UI_FENCE);
  if (openFence !== -1) {
    return partial.slice(0, openFence);
  }
  const tail = partial.match(PARTIAL_UI_FENCE);
  return tail ? partial.slice(0, tail.index) : partial;
}
